import { memo, useCallback, useEffect, useMemo } from "react";

import DragHandleIcon from "@material-ui/icons/DragHandle";
import Checkbox from "antd/lib/checkbox/Checkbox";
import classnames from "classnames";
import { debounce } from "lodash";
import styled from "styled-components";
import { getPerformanceRatioDisplayName } from "utils/data-table/getProductDisplayName";
import { getProductLabel } from "utils/getProductLabel";

import { useHover } from "hooks";

import { IGroupBy } from "models";
import { EntityKind } from "models/entityKind";
import { IGroupByListColumn } from "models/groupBy";

import FieldGroup from "./FieldGroup";
import FieldListItemInfo from "./FieldListItemInfo";
import FieldOverflowTooltip from "./FieldOverflowTooltip";
import UserFieldAction from "./UserFieldAction";

interface FieldSelectionData {
  field: IGroupByListColumn;
  onChange?: (
    value: IGroupBy | IGroupByListColumn | Record<string, unknown>,
    isForecastToggleOn?: boolean,
    selectedForecastFolder?: string,
    selectedForecastFolderName?: string,
    selectedPdenSource?: number
  ) => void;
  isForecastToggleOn?: boolean;
  selectedForecastFolder?: string;
  selectedForecastFolderName?: string;
  selectedPdenSource?: number;
}

const OrgDefinedColumnPrefix = "org_focus";
const UserDefinedColumnPrefix = "my_focus";
const OrgDefinedMidstreamColumnPrefix = "org_midstream";
const UserDefinedMidstreamColumnPrefix = "my_midstream";
const ProjectDefinedColumnPrefix = "project_focus";

export type FieldListItemT = {
  value: IGroupByListColumn;
  dragEnabled: boolean;
  groups: FieldGroup[];
  shortcutsEnabled: boolean;
  active?: boolean;
  itemRef?: (el: Maybe<HTMLElement>) => void;
  onFieldClick?: (fieldData: FieldSelectionData) => void;
  onFieldDoubleClick?: (fieldData: FieldSelectionData) => void;
  isDisablingManagement?: boolean;
  // Store-derived data (passed from container)
  isCustomColumn: boolean;
  onChange?: (
    value: IGroupBy | IGroupByListColumn | Record<string, unknown>,
    isForecastToggleOn?: boolean,
    selectedForecastFolder?: string,
    selectedForecastFolderName?: string,
    selectedPdenSource?: number
  ) => void;
  checkedFields: readonly string[];
  customFieldsEditToggle: boolean;
  selectedField: Maybe<IGroupByListColumn>;
  isForecastToggleOn: boolean;
  selectedForecastFolder: string;
  selectedForecastFolderName: string;
  selectedPdenSource: number;
  isFiltering: boolean;
  categoryColors: Record<string, string>;
  entityKind: EntityKind;
  onCheckedFieldsChange: (fields: readonly string[]) => void;
  onHoveredFieldChange: (field: Maybe<IGroupByListColumn>) => void;
};

function FieldListItem({
  value,
  dragEnabled,
  groups,
  shortcutsEnabled,
  active = false,
  itemRef,
  onFieldClick,
  onFieldDoubleClick,
  isDisablingManagement = false,
  isCustomColumn,
  onChange,
  checkedFields,
  customFieldsEditToggle,
  selectedField,
  isForecastToggleOn,
  selectedForecastFolder,
  selectedForecastFolderName,
  selectedPdenSource,
  isFiltering,
  categoryColors,
  entityKind,
  onCheckedFieldsChange,
  onHoveredFieldChange
}: FieldListItemT) {
  const { tooltip, title, product, defaultUnit, property, isDisabled } = value;

  const isUserDefinedField =
    property?.toLowerCase().startsWith(OrgDefinedColumnPrefix) ||
    property?.toLowerCase().startsWith(UserDefinedColumnPrefix) ||
    property?.toLowerCase().startsWith(OrgDefinedMidstreamColumnPrefix) ||
    property?.toLowerCase().startsWith(UserDefinedMidstreamColumnPrefix) ||
    property?.toLowerCase().startsWith(ProjectDefinedColumnPrefix);

  const [hoverRef, isHovered] = useHover();

  const selectField = useCallback(() => {
    const newValue = { ...value };

    // Use direct callback instead of CustomEvent
    if (onFieldClick) {
      onFieldClick({
        field: newValue,
        onChange,
        isForecastToggleOn,
        selectedForecastFolder,
        selectedForecastFolderName,
        selectedPdenSource
      });
    }
  }, [
    value,
    onChange,
    isForecastToggleOn,
    selectedForecastFolder,
    selectedForecastFolderName,
    selectedPdenSource,
    onFieldClick
  ]);

  // Used for arrow key navigation between focus fields in the group by list
  useEffect(() => {
    const handleArrowKeyPress = (event: KeyboardEvent) => {
      // If the selected field is not the current field, or the component is not being rendered by the preset dropdown, return
      if (selectedField?.id !== value.id || !groups || !shortcutsEnabled) return;

      let nextField: IGroupByListColumn;
      let nextGroupIndex: number;
      let nextFieldIndex: number;

      // Find the index of the current field in the groups array
      const currentGroupIndex = groups.findIndex((group) =>
        group?.fields.some((field) => field.id === value.id)
      );
      const currentFieldIndex = groups[currentGroupIndex]?.fields.findIndex(
        (field) => field.id === value.id
      );

      if (currentGroupIndex === -1 || currentFieldIndex === -1) return;

      // Logic for navigating to the next field with arrow down key
      if (event.ctrlKey && event.key === "ArrowDown") {
        const isNextFieldInSameGroup: boolean =
          currentFieldIndex < groups[currentGroupIndex]?.fields.length - 1;
        const isNextFieldLastItemInSameGroup: boolean =
          currentGroupIndex < groups.length - 1;
        if (isNextFieldInSameGroup) {
          nextGroupIndex = currentGroupIndex;
          nextFieldIndex = currentFieldIndex + 1;
        } else if (isNextFieldLastItemInSameGroup) {
          nextGroupIndex = currentGroupIndex + 1;
          nextFieldIndex = 0;
        } else {
          nextGroupIndex = 0;
          nextFieldIndex = 0;
        }
        // Logic for navigating to the next field with arrow up key
      } else if (event.ctrlKey && event.key === "ArrowUp") {
        const isNextFieldInSameGroup: boolean = currentFieldIndex > 0;
        const isNextFieldFirstItemInSameGroup: boolean = currentGroupIndex > 0;
        if (isNextFieldInSameGroup) {
          nextGroupIndex = currentGroupIndex;
          nextFieldIndex = currentFieldIndex - 1;
        } else if (isNextFieldFirstItemInSameGroup) {
          nextGroupIndex = currentGroupIndex - 1;
          nextFieldIndex = groups[nextGroupIndex]?.fields.length - 1;
        } else {
          nextGroupIndex = groups.length - 1;
          nextFieldIndex = groups[nextGroupIndex]?.fields.length - 1;
        }
      }

      // If the next field index is defined, update the selected field
      if (nextGroupIndex !== undefined && nextFieldIndex !== undefined) {
        nextField = groups[nextGroupIndex]?.fields[nextFieldIndex];
        const newValue = { ...nextField };
        if (onChange) {
          onChange(
            newValue,
            isForecastToggleOn,
            selectedForecastFolder,
            selectedForecastFolderName,
            selectedPdenSource
          );
        }
      }
    };

    // Debounce the arrow key press event to prevent multiple events from firing
    const debouncedHandleArrowKeyPress = debounce(handleArrowKeyPress, 200);
    window.addEventListener("keydown", debouncedHandleArrowKeyPress);
    return () => {
      window.removeEventListener("keydown", debouncedHandleArrowKeyPress);
    };
  });

  const rootClassName = classnames({
    selected: value.property === selectedField?.property,
    "keyboard-focused": active
  });

  const onClick = useCallback(
    (e) => {
      if (customFieldsEditToggle && isCustomColumn) {
        return;
      }
      return isDisabled ? e.preventDefault() : selectField();
    },
    [customFieldsEditToggle, isCustomColumn, isDisabled, selectField]
  );

  const onDoubleClick = useCallback(
    (e) => {
      if (customFieldsEditToggle && isCustomColumn) {
        return;
      }
      if (isDisabled) {
        e.preventDefault();
        return;
      }
      if (onFieldDoubleClick) {
        const newValue = { ...value };
        onFieldDoubleClick({
          field: newValue,
          onChange,
          isForecastToggleOn,
          selectedForecastFolder,
          selectedForecastFolderName,
          selectedPdenSource
        });
      }
    },
    [
      customFieldsEditToggle,
      isCustomColumn,
      isDisabled,
      onFieldDoubleClick,
      value,
      onChange,
      isForecastToggleOn,
      selectedForecastFolder,
      selectedForecastFolderName,
      selectedPdenSource
    ]
  );

  const productContextTitle =
    isFiltering && product
      ? `${getPerformanceRatioDisplayName(getProductLabel(product, entityKind))} ${title}`
      : title;
  const dynamicTitle =
    isUserDefinedField && defaultUnit
      ? `${productContextTitle} (${defaultUnit})`
      : productContextTitle;

  const titleId = `title-${property}`;

  const isChecked = useMemo(
    () => checkedFields.includes(value.id),
    [checkedFields, value.id]
  );

  const handleCheckboxChange = useCallback(() => {
    onCheckedFieldsChange(
      isChecked
        ? checkedFields.filter((checkedItem) => checkedItem !== value.id)
        : [...checkedFields, value.id]
    );
  }, [isChecked, checkedFields, value.id, onCheckedFieldsChange]);

  return (
    <Wrapper
      ref={(el) => {
        hoverRef.current = el;
        if (itemRef) itemRef(el);
      }}
      role="listitem"
      tabIndex={0}
      className={`${rootClassName} field-list-item`}
      onClick={(e) => onClick(e)}
      onDoubleClick={(e) => onDoubleClick(e)}
      onMouseEnter={() => onHoveredFieldChange(value)}
      onMouseLeave={() => onHoveredFieldChange(null)}
      isDisabled={isDisabled}>
      <FieldListItemDisplay>
        {isCustomColumn && <Indent />}
        {isCustomColumn && customFieldsEditToggle && (
          <>
            <DragHandleIconWrapper dragEnabled={dragEnabled}>
              <DragHandleIcon />
            </DragHandleIconWrapper>

            <CheckboxItem
              checked={isChecked}
              onClick={(evt) => evt.stopPropagation()}
              onChange={handleCheckboxChange}></CheckboxItem>
          </>
        )}
        {isFiltering && <FieldColorPatch color={categoryColors[value.group]} />}
        <FieldOverflowTooltip title={dynamicTitle} titleId={titleId}>
          <Title id={titleId}>{dynamicTitle}</Title>
        </FieldOverflowTooltip>
        <FieldListItemInfo value={tooltip} />
      </FieldListItemDisplay>
      {!isDisablingManagement && (
        <UserFieldActionWrapper className="user-action" visible={isHovered}>
          <UserFieldAction visible={isHovered} field={value} entityKind={entityKind} />
        </UserFieldActionWrapper>
      )}
    </Wrapper>
  );
}

export default memo(FieldListItem);

const CheckboxItem = styled(Checkbox)`
  overflow-x: visible !important;
  .ant-checkbox-checked .ant-checkbox-inner {
    background-color: var(--color-primary);
    border-color: var(--color-primary);
  }
`;

const Indent = styled.div`
  min-width: 19px;
`;
const Wrapper = styled.div`
  min-width: 0;
  display: flex;
  align-items: center;
  gap: var(--space-1);
  border-radius: var(--border-radius);
  color: var(--color-action-button);
  font-size: 1.4rem;
  font-weight: var(--fontWeightMedium);
  padding: 2px 8px;
  cursor: ${(props) => (props.isDisabled ? "not-allowed" : "pointer")};
  user-select: none;

  &:hover,
  &.selected {
    color: ${(props) =>
      props.isDisabled ? "var(--color-action-button)" : "var(--color-text)"};
    background-color: ${(props) =>
      props.isDisabled ? "var(--color-white)" : "var(--color-accent-hover)"};
  }

  &:focus {
    outline: none; /* Remove default focus outline */
    box-shadow: none; /* Remove focus box shadow */
  }
`;

const FieldListItemDisplay = styled.div`
  display: flex;
  align-items: center;
  min-width: 0;
  flex: 1 1 0%;
  gap: var(--space-1);
`;

const Title = styled.span`
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
`;

const UserFieldActionWrapper = styled.span<{ visible?: boolean }>`
  display: inline-flex;
  visibility: ${({ visible }) => (visible ? "visible" : "hidden")};
`;

const DragHandleIconWrapper = styled.span`
  visibility: ${({ dragEnabled }) => (dragEnabled ? "visible" : "hidden")};
  color: #c9d1d2;
  font-size: 16px;
`;

const FieldColorPatch = styled.div`
  width: 8px;
  height: 8px;
  background-color: ${({ color }) => color};
  border-radius: 2px;
  flex-shrink: 0;
`;
